import { StepType, TourProvider } from "@reactour/tour";
import { useContext, useEffect, useState } from "react";
import { Provider as AlertProvider, positions, transitions } from "react-alert";
import AlertTemplate from "react-alert-template-basic";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import App from "./App";
import { BasicAuthForm } from "./components";
import ScriptBlock from "./components/ScriptBlock";
import {
  CadviewerContext,
  CadviewerContextProvider,
} from "./context/Cadviewer.Context";
import { RuntimeConfigProvider } from "./context/RuntimeConfig.Context";
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import config from "./utils/config";
import { useLocalStorage } from "./utils/useLocalStorage";
import { NotFound } from "./views/NotFound/NotFound";
import { Profile } from "./views/Profile";
import Settings from "./views/Settings";
import { Users } from "./views/Users";
import ValidateEmail from "./views/ValidateEmail";
import LogAnalyzer from "./views/LogAnalyzer/LogAnalyzer";
import { DistanceQueryPage } from "./components/DistanceQuery";

// "ResizeObserver loop completed with undelivered notifications" is a benign
// browser notice: an observer callback changed the layout, so the browser
// deferred the remaining notifications. Third-party libraries that observe
// layout (react-resizable-panels, Headless UI, CADViewer) can emit it too, and
// the CRA dev overlay escalates it into a full-screen "Uncaught runtime error".
// Only this exact message is swallowed - every other error still surfaces.
const RESIZE_OBSERVER_LOOP_MESSAGES = [
  "ResizeObserver loop completed with undelivered notifications",
  "ResizeObserver loop limit exceeded",
];

window.addEventListener(
  "error",
  (event) => {
    const message = event.message ?? "";
    if (RESIZE_OBSERVER_LOOP_MESSAGES.some((m) => message.includes(m))) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  },
  true
);

// Wrapper for each demo that forces re-render with the right config
const DemoWrapper: React.FC<{ demoId: string }> = ({ demoId }) => {
  return <App key={demoId} />;
};

const steps = [
  !config.hideLeftHamburgerMenu
    ? {
      selector: "#hamburger-menu",
      content: (
        <div>
          This part is to expose linked content with Spaces on the floorplan.
          (used for Visual Query but not for CADViewer demo)
          <img src="/cvlogo.svg" alt="cvlogo" />
          <a
            href="https://www.visualquery.com"
            target="_blank"
            rel="noreferrer"
          >
            Learn more about Visual Query
          </a>
        </div>
      ),
    }
    : undefined,
  config.enableLoginFeature
    ? {
      selector: "#register-button",
      content:
        "Use the Register button to register and start the trial. \nYou can also login if you already have an account. The trial is free and no credit card is required, and you can cancel anytime. \nThe account is active for 7 days. ",
    }
    : undefined,
  config.enableLoginFeature
    ? {
      selector: "#login-button",
      content:
        "Once you have verified your email address, you can use the Login button to login to your account",
    }
    : undefined,
  config.enableRightFileModalPanel
    ? {
      selector: "#folder-structure-button",
      content:
        "Use the Folder Structure button to browse the available demo files and upload your own files. Note that there is a limit of 8 files and 10MB total file-size per user. Have fun! -and contact us if you need an extension - we are happy to help!",
    }
    : undefined,
];

const BuildRoute = () => {
  const { user, setUser } = useContext(CadviewerContext);
  const { getItem } = useLocalStorage();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!mounted) {
      setUser(
        getItem("auth_user") ? JSON.parse(getItem("auth_user") as string) : null
      );
      setMounted(true);
    }
  }, [getItem, setUser, mounted]);

  if (!mounted) {
    return <div />;
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Default route */}
        <Route path={"/"} element={<DemoWrapper demoId="default" />} />

        {/* Demo routes */}
        {config.enableDemoNavigation && (
          <>
            <Route
              path={"/visualqueryai"}
              element={<DemoWrapper demoId="visualqueryai" />}
            />
            <Route path={"/demo2"} element={<DemoWrapper demoId="demo2" />} />
            <Route path={"/demo3"} element={<DemoWrapper demoId="demo3" />} />
            <Route path={"/demo4"} element={<DemoWrapper demoId="demo4" />} />
            <Route
              path={"/snowflake_realcomm"}
              element={<DemoWrapper demoId="snowflake_realcomm" />}
            />
            <Route
              path={"/snowflake"}
              element={<DemoWrapper demoId="snowflake" />}
            />
            <Route
              path={"/urbanedge"}
              element={<DemoWrapper demoId="urbanedge" />}
            />
            <Route
              path={"/federalrealty"}
              element={<DemoWrapper demoId="federalrealty" />}
            />
            <Route
              path={"/uic_edu"}
              element={<DemoWrapper demoId="uic_edu" />}
            />
            <Route
              path={"/insuremep"}
              element={<DemoWrapper demoId="insuremep" />}
            />
            <Route
              path={"/vizquery_nextgen"}
              element={<DemoWrapper demoId="vizquery_nextgen" />}
            />
            <Route
              path={"/public_demo"}
              element={<DemoWrapper demoId="public_demo" />}
            />
            <Route
              path={"/total"}
              element={<DemoWrapper demoId="total" />}
            />
            <Route
              path={"/docuex.co.uk"}
              element={<DemoWrapper demoId="docuex_co_uk" />}
            />
          </>
        )}

        {/* Existing routes */}
        <Route path={"/validate-email/:token"} element={<ValidateEmail />} />
        <Route
          path={"/:file1/:file2"}
          element={<DemoWrapper demoId="default" />}
        />
        {user && <Route path={"/profile"} element={<Profile />} />}
        {user?.role === "admin" && (
          <Route path={"/settings"} element={<Settings />} />
        )}
        {user?.role === "admin" && (
          <Route path={"/users"} element={<Users />} />
        )}
        {/* Distance Query full-page — canonical top-level route, reachable from any demo */}
        <Route path={"/distance-query"} element={<DistanceQueryPage />} />
        {/* Backward-compatible nested route */}
        <Route
          path={"/public_demo/distance-query"}
          element={<DistanceQueryPage />}
        />
        <Route path={"/log-analyzer"} element={<LogAnalyzer />} />
        <Route path={"*"} element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

// optional configuration
const options = {
  // you can also just use 'bottom center'
  position: positions.BOTTOM_RIGHT,
  timeout: 5000,
  offset: "30px",
  // you can also just use 'scale'
  transition: transitions.SCALE,
};

root.render(
  <RuntimeConfigProvider>
    <BasicAuthForm>
      <TourProvider
        steps={steps.filter((step) => step !== undefined) as StepType[]}
        scrollSmooth
        styles={{
          popover: (base) => ({
            ...base,
            "--reactour-accent": "#0f3557",
            borderRadius: 10,
          }),
          badge: (base) => ({ ...base, left: "auto", right: "-0.8125em" }),
          close: (base) => ({ ...base, right: "auto", left: 8, top: 8 }),
        }}
      >
        <AlertProvider template={AlertTemplate} {...options}>
          <ScriptBlock />
          <CadviewerContextProvider>
            <BuildRoute />
          </CadviewerContextProvider>
        </AlertProvider>
      </TourProvider>
    </BasicAuthForm>
  </RuntimeConfigProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
